import { z } from "zod";
import { PERMISSIONS } from "./permissions.js";

/**
 * Developer platform contracts: personal access tokens and outbound webhooks.
 * Shared by the API (validation) and the Settings > Apps > Developer UI.
 */

export const WEBHOOK_EVENTS = [
  "constituent.created",
  "constituent.updated",
  "constituent.merged",
  /** CT-RM-21: a tag was newly added to a constituent (one event per tag) */
  "constituent.tag_added",
  "gift.created",
  "gift.reversed",
  "pledge.created",
  "batch.committed",
] as const;
export type WebhookEvent = (typeof WEBHOOK_EVENTS)[number];
/** what each event means, for the endpoint editor's event filter */
export const WEBHOOK_EVENT_INFO: Record<WebhookEvent, { group: string; description: string }> = {
  "constituent.created": { group: "Constituents", description: "A record was added (form, import, API, integration)" },
  "constituent.updated": { group: "Constituents", description: "Any edit: form, API, import, merge, donor portal, automation" },
  "constituent.merged": { group: "Constituents", description: "A duplicate was merged into a surviving record" },
  "constituent.tag_added": { group: "Constituents", description: "A tag was newly added (one event per tag)" },
  "gift.created": { group: "Giving", description: "A gift was recorded" },
  "gift.reversed": { group: "Giving", description: "A gift was reversed" },
  "pledge.created": { group: "Giving", description: "A pledge was created" },
  "batch.committed": { group: "Giving", description: "A gift batch was committed" },
};

/** sent only by "Send test event"; not subscribable */
export const WEBHOOK_TEST_EVENT = "webhook.test";

/** subscribable values: a catalog event or "*" (everything) */
export const zWebhookEventFilter = z.string().refine((e) => e === "*" || (WEBHOOK_EVENTS as readonly string[]).includes(e), "unknown event");

/** the longest a token may live, in days */
export const API_TOKEN_MAX_DAYS = 3650;
/** settings shows an "expires soon" warning this many days ahead */
export const API_TOKEN_EXPIRY_WARNING_DAYS = 7;

export const zCreateApiToken = z
  .object({
    name: z.string().trim().min(1).max(100),
    scopes: z.array(z.enum([...PERMISSIONS, "*"] as [string, ...string[]])).min(1),
    expiresInDays: z.number().int().min(1).max(API_TOKEN_MAX_DAYS).optional(),
    /** an explicit expiry date/time (ISO); alternative to expiresInDays */
    expiresAt: z
      .string()
      .datetime({ offset: true })
      .refine((v) => Date.parse(v) > Date.now(), "expiry must be in the future")
      .refine((v) => Date.parse(v) <= Date.now() + API_TOKEN_MAX_DAYS * 86_400_000, "expiry is too far in the future")
      .optional(),
  })
  .refine((v) => !(v.expiresInDays && v.expiresAt), { message: "give expiresInDays or expiresAt, not both", path: ["expiresAt"] });
export type CreateApiToken = z.infer<typeof zCreateApiToken>;

export const zCreateWebhook = z.object({
  url: z.string().trim().url().max(500),
  events: z.array(zWebhookEventFilter).min(1),
});
export type CreateWebhook = z.infer<typeof zCreateWebhook>;

export const zUpdateWebhook = z
  .object({
    url: z.string().trim().url().max(500).optional(),
    events: z.array(zWebhookEventFilter).min(1).optional(),
    enabled: z.boolean().optional(),
  })
  .strict();
export type UpdateWebhook = z.infer<typeof zUpdateWebhook>;

export interface ApiTokenSummary {
  id: string;
  name: string;
  prefix: string;
  scopes: string[];
  lastUsedAt: string | null;
  /** client IP of the most recent request made with the token */
  lastUsedIp: string | null;
  expiresAt: string | null;
  createdAt: string;
}

export type ApiTokenExpiryState = "never" | "ok" | "expiring" | "expired";

/** "expiring" = within API_TOKEN_EXPIRY_WARNING_DAYS; shared by settings, the notice banner and the API */
export function apiTokenExpiryState(expiresAt: string | Date | null | undefined, now: number = Date.now()): ApiTokenExpiryState {
  if (!expiresAt) return "never";
  const at = typeof expiresAt === "string" ? Date.parse(expiresAt) : expiresAt.getTime();
  if (at <= now) return "expired";
  return at - now <= API_TOKEN_EXPIRY_WARNING_DAYS * 86_400_000 ? "expiring" : "ok";
}

export interface CreatedApiToken {
  id: string;
  /** raw token; returned exactly once */
  token: string;
  scopes: string[];
}

/** after a rotation the previous signing secret keeps verifying (dual signatures) this long */
export const WEBHOOK_SECRET_GRACE_HOURS = 24;
/**
 * Auto-disable: an endpoint is switched off once it has failed this many delivery
 * attempts in a row AND the streak has lasted at least WEBHOOK_AUTO_DISABLE_MIN_HOURS,
 * so a short outage never disables it. Any successful delivery resets the streak.
 */
export const WEBHOOK_AUTO_DISABLE_FAILURES = 20;
export const WEBHOOK_AUTO_DISABLE_MIN_HOURS = 6;

export type WebhookDisabledReason = "manual" | "failure_streak";

export interface WebhookSummary {
  id: string;
  url: string;
  events: string[];
  enabled: boolean;
  /** "rest_hook" = created by an integration (e.g. Zapier) via POST /hooks */
  kind?: "webhook" | "rest_hook";
  /** why a disabled endpoint is off; "failure_streak" = switched off automatically */
  disabledReason?: WebhookDisabledReason | null;
  disabledAt?: string | null;
  /** failed attempts in a row (0 after any success) */
  consecutiveFailures?: number;
  failingSince?: string | null;
  secretRotatedAt?: string | null;
  /** while in the future, deliveries carry signatures from both the new and the previous secret */
  previousSecretExpiresAt?: string | null;
  createdAt: string;
}

export interface RotatedWebhookSecret {
  id: string;
  /** new signing secret; returned exactly once */
  secret: string;
  /** the previous secret keeps verifying (dual signatures) until this time */
  previousSecretExpiresAt: string;
}

/** in-app notices: endpoints switched off automatically, and the caller's tokens near or past expiry */
export interface IntegrationNotices {
  disabledWebhooks: { id: string; url: string; disabledAt: string | null; consecutiveFailures: number }[];
  expiringTokens: { id: string; name: string; expiresAt: string; state: "expiring" | "expired" }[];
}

export interface CreatedWebhook {
  id: string;
  url: string;
  events: string[];
  /** signing secret; returned exactly once */
  secret: string;
}

/** "simulated": a sandbox org's delivery to an external host, recorded but never sent (CT-RM-22) */
export type WebhookDeliveryStatus = "pending" | "retrying" | "delivered" | "failed" | "cancelled" | "simulated";

export const WEBHOOK_DELIVERY_STATUSES = ["pending", "retrying", "delivered", "failed", "cancelled", "simulated"] as const;

/** delivery log search: GET /platform/webhooks/:id/deliveries */
export const zWebhookDeliveryQuery = z.object({
  event: z.string().trim().max(100).optional(),
  status: z.enum(WEBHOOK_DELIVERY_STATUSES).optional(),
  /** ISO date or date-time; inclusive lower bound on createdAt */
  from: z.string().trim().refine((v) => !Number.isNaN(Date.parse(v)), "invalid date").optional(),
  /** ISO date or date-time; a bare date (YYYY-MM-DD) includes that whole day */
  to: z.string().trim().refine((v) => !Number.isNaN(Date.parse(v)), "invalid date").optional(),
  /** a delivery id, or text in the payload (e.g. a constituent id) */
  q: z.string().trim().max(200).optional(),
  limit: z.coerce.number().int().min(1).max(200).default(100),
});
export type WebhookDeliveryQuery = z.infer<typeof zWebhookDeliveryQuery>;

export interface WebhookDeliverySummary {
  id: string;
  event: string;
  status: WebhookDeliveryStatus;
  attempts: number;
  responseStatus: number | null;
  lastError: string | null;
  deliveredAt: string | null;
  createdAt: string;
}

export interface WebhookTestResult {
  deliveryId: string;
  status: WebhookDeliveryStatus;
  responseStatus: number | null;
  error: string | null;
}
